/* eslint-disable class-methods-use-this */
import AgoraRTC, {
  CameraVideoTrackInitConfig,
  ClientConfig,
  ClientRole,
  IAgoraRTCClient,
  IAgoraRTCRemoteUser,
  ICameraVideoTrack,
  ILocalTrack,
  ILocalVideoTrack,
  IMicrophoneAudioTrack,
  IRemoteAudioTrack,
  IRemoteVideoTrack,
  MicrophoneAudioTrackInitConfig,
  UID,
  VideoPlayerConfig
} from 'agora-rtc-sdk-ng';
import _uniq from 'lodash/uniq';

import config from 'config';

import { FacingMode, MediaType, OnStageUser } from 'types';
import { getClientRoleOptions, getTranscodingConfig, getVideoEncoderConfig } from 'utils';

abstract class RTCService {
  public static microphoneTrack: IMicrophoneAudioTrack | null;

  public static cameraTrack: ICameraVideoTrack | null;

  public static client: IAgoraRTCClient;

  public static clientRole: ClientRole;

  public static facingMode: FacingMode;

  public static streamingEndpoints: string[];

  public static init(clientConfig?: ClientConfig) {
    if (!RTCService.client) {
      RTCService.client = AgoraRTC.createClient({
        mode: 'live',
        codec: 'vp8',
        clientRoleOptions: { level: 1 },
        ...clientConfig
      });
    }
    RTCService.facingMode = FacingMode.FRONT;
    RTCService.streamingEndpoints = [];
  }

  public static async createMicrophoneTrack({
    encoderConfig = 'speech_standard',
    ...trackConfig
  }: MicrophoneAudioTrackInitConfig = {}): Promise<IMicrophoneAudioTrack> {
    const microphoneTrack = await AgoraRTC.createMicrophoneAudioTrack({
      encoderConfig,
      ...trackConfig
    });

    RTCService.microphoneTrack = microphoneTrack;

    return microphoneTrack;
  }

  public static async createCameraTrack({
    facingMode = 'user',
    ...trackConfig
  }: CameraVideoTrackInitConfig = {}): Promise<ICameraVideoTrack> {
    const allDevices = await AgoraRTC.getCameras();
    const filteredDevices = allDevices.filter(device => !device.label.toLowerCase().match(/infrared/g));
    const targetDevice =
      facingMode === 'user'
        ? filteredDevices.find(device => device.label.toLowerCase().match(/front/g))
        : filteredDevices.find(device => device.label.toLowerCase().match(/back/g));
    const cameraTrack = await AgoraRTC.createCameraVideoTrack({
      facingMode,
      encoderConfig: getVideoEncoderConfig(),
      cameraId: targetDevice?.deviceId,
      ...trackConfig
    });

    RTCService.cameraTrack = cameraTrack;

    return cameraTrack;
  }

  public static async closeLocalTrack(localTrack: ILocalTrack): Promise<void> {
    if (localTrack.isPlaying) {
      localTrack.stop();
    }

    localTrack.close();
  }

  public static playAudio(track: IRemoteAudioTrack) {
    track.play();
  }

  public static stopAudio(track: IRemoteAudioTrack) {
    track.stop();
  }

  public static playVideo(
    track: ILocalVideoTrack | IRemoteVideoTrack,
    element: HTMLElement | string,
    options?: VideoPlayerConfig
  ) {
    track.play(element, options);
  }

  public static stopVideo(track: ILocalVideoTrack | IRemoteVideoTrack) {
    track.stop();
  }

  public static getRemoteNetworkQuality() {
    const network = RTCService.client.getRemoteNetworkQuality();

    return network;
  }

  public static getRTCStats() {
    const stats = RTCService.client.getRTCStats();

    return stats;
  }

  public static async setClientRole(clientRole: ClientRole): Promise<void> {
    const options = getClientRoleOptions(clientRole);

    await RTCService.client.setClientRole(clientRole, options);

    RTCService.clientRole = clientRole;
  }

  public static async join(channelName: string, uid: UID | null = null, token: string | null = null): Promise<UID> {
    if (['CONNECTED', 'CONNECTING', 'RECONNECTING'].includes(RTCService.client.connectionState)) {
      await RTCService.leave(channelName);
    }

    const uidResponse = await RTCService.client.join(config.agora.appId, channelName, token, uid);

    return uidResponse;
  }

  public static async subscribe(remoteUser: IAgoraRTCRemoteUser, mediaType: MediaType): Promise<void> {
    await RTCService.client.subscribe(remoteUser, mediaType);
  }

  public static async unsubscribe(remoteUser: IAgoraRTCRemoteUser, mediaType: MediaType): Promise<void> {
    await RTCService.client.unsubscribe(remoteUser, mediaType);
  }

  public static async leave(channelName: string): Promise<void> {
    if (['DISCONNECTED', 'DISCONNECTING'].includes(RTCService.client.connectionState)) {
      return;
    }

    await RTCService.client.leave();
    await RTCService.closeLocalTracks();
    await RTCService.setClientRole('audience');

    RTCService.client.removeAllListeners(channelName);
    RTCService.streamingEndpoints = [];
    RTCService.facingMode = FacingMode.FRONT;
  }

  public static async closeLocalTracks(): Promise<void> {
    const promises: Promise<void>[] = [];

    if (RTCService.microphoneTrack) {
      const promise = RTCService.closeLocalTrack(RTCService.microphoneTrack);

      promises.push(promise);
    }

    if (RTCService.cameraTrack) {
      const promise = RTCService.closeLocalTrack(RTCService.cameraTrack);

      promises.push(promise);
    }

    await Promise.all(promises);

    RTCService.microphoneTrack = null;
    RTCService.cameraTrack = null;
  }

  public static enableAudioVolumeIndicator(): void {
    RTCService.client.enableAudioVolumeIndicator();
  }

  public static async publishLocalTrack(localTrack: ILocalTrack): Promise<void> {
    const alreadyPublished = RTCService.client.localTracks.find(
      track => track.getTrackId() === localTrack.getTrackId()
    );

    if (alreadyPublished) return;

    await RTCService.client.publish(localTrack);
  }

  public static async unpublishLocalTrack(localTrack: ILocalTrack): Promise<void> {
    await RTCService.client.unpublish(localTrack);
  }

  public static async createLocalTracks(
    withAudio?: boolean,
    withVideo?: boolean
  ): Promise<[IMicrophoneAudioTrack | null, ICameraVideoTrack | null]> {
    // In case mic test was muted on backstage, unmute microphoneTrack before publishing
    if (withAudio && RTCService.microphoneTrack?.muted) {
      await RTCService.unmuteMicrophone();
    }

    if (withAudio && !RTCService.microphoneTrack) {
      RTCService.microphoneTrack = await RTCService.createMicrophoneTrack({});
    }

    // In case camera test was disabled on backstage, enable cameraTrack before publishing
    if (withVideo && RTCService.cameraTrack && !RTCService.cameraTrack.enabled) {
      await RTCService.cameraTrack.setEnabled(true);
    }

    if (withVideo && !RTCService.cameraTrack) {
      RTCService.cameraTrack = await RTCService.createCameraTrack({
        facingMode: RTCService.facingMode === FacingMode.FRONT ? 'user' : 'environment'
      });
    }

    return [RTCService.microphoneTrack, RTCService.cameraTrack];
  }

  public static async changeFacingMode(facingMode: FacingMode): Promise<FacingMode> {
    if (RTCService.cameraTrack) {
      await RTCService.unpublishLocalTrack(RTCService.cameraTrack);
      await RTCService.closeLocalTrack(RTCService.cameraTrack);
    }

    RTCService.facingMode = facingMode;
    RTCService.cameraTrack = await RTCService.createCameraTrack({
      facingMode: facingMode === FacingMode.FRONT ? 'user' : 'environment'
    });

    RTCService.publishLocalTrack(RTCService.cameraTrack);

    return RTCService.facingMode;
  }

  public static async muteMicrophone(): Promise<void> {
    if (!RTCService.microphoneTrack) return;

    await RTCService.microphoneTrack.setMuted(true);
  }

  public static async unmuteMicrophone(): Promise<void> {
    if (!RTCService.microphoneTrack) {
      RTCService.microphoneTrack = await RTCService.createMicrophoneTrack();
      await RTCService.publishLocalTrack(RTCService.microphoneTrack);
      await RTCService.microphoneTrack.setMuted(false);
    } else {
      await RTCService.microphoneTrack.setMuted(false);
    }
  }

  public static async muteCamera(): Promise<void> {
    if (!RTCService.cameraTrack) return;

    await RTCService.cameraTrack.setEnabled(false);
  }

  public static async unmuteCamera(): Promise<void> {
    if (!RTCService.cameraTrack) {
      RTCService.cameraTrack = await RTCService.createCameraTrack();
      await RTCService.publishLocalTrack(RTCService.cameraTrack);
    }

    await RTCService.cameraTrack.setEnabled(true);
  }

  public static async startLiveStreaming(rtmpUrl: string, stageUsers: OnStageUser[]): Promise<void> {
    if (RTCService.clientRole !== 'host') return;

    const transcodingConfig = getTranscodingConfig(stageUsers);

    await RTCService.client.setLiveTranscoding(transcodingConfig);
    await RTCService.client.startLiveStreaming(rtmpUrl, true);

    RTCService.streamingEndpoints = _uniq([...RTCService.streamingEndpoints, rtmpUrl]);
  }

  public static async stopLiveStreaming(rtmpUrl: string): Promise<void> {
    if (RTCService.clientRole !== 'host') return;

    await RTCService.client.stopLiveStreaming(rtmpUrl);

    RTCService.streamingEndpoints = RTCService.streamingEndpoints.filter(url => url !== rtmpUrl);
  }

  public static async updateTranscodingConfig(stageUsers: OnStageUser[]): Promise<void> {
    const transcodingConfig = getTranscodingConfig(stageUsers);

    await RTCService.client.setLiveTranscoding(transcodingConfig);
  }
}

export default RTCService;
